import { IconButton, Box, Button, Center, Flex, HStack, Text, VStack } from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, ChevronLeft } from 'lucide-react';
import { trpcTenant } from '@duke-hq/app-provider-web/tenant/trpcTenant';
import { StateIcon } from './StateIcon';
import { formatCurrency } from '@duke-hq/lib/numberFormats';
import { useMonthNavigation, type SortFilters } from '../hooks/useMonthNavigation';

const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_ORDER_BY = 'totalSpent';

function useSiteLeaderboard(filters: SortFilters, currentLimit: number) {
  const queryResult =
    trpcTenant.groupRewards.listSiteLeaderboardWithInfiniteScroll.useInfiniteQuery(
      { filters, limit: currentLimit },
      {
        getNextPageParam: (lastPage) => lastPage.nextCursor,
        placeholderData: (prev) => prev,
        staleTime: 5 * 60 * 1000,
      },
    );

  const sites = useMemo(
    () => queryResult.data?.pages.flatMap((page) => page.items) ?? [],
    [queryResult.data],
  );

  return {
    ...queryResult,
    sites,
  };
}

function SitedLeaderboardWidgetRow({
  site,
}: {
  site: {
    siteId: string;
    siteName: string;
    siteState: string;
    totalAllocated?: number;
    totalUsers?: number;
  };
}) {
  return (
    <HStack spacing={4} width="full" align="center" justify="space-between">
      <HStack spacing={4} align="center">
        {site.siteState != null ? <StateIcon siteState={site.siteState} /> : null}
        <VStack align="start" spacing={0}>
          <Text fontSize="sm" fontWeight="medium">
            {site.siteName}
          </Text>
          <Text fontSize="xs" color="fg.muted">
            {site.siteState}
          </Text>
        </VStack>
      </HStack>
      <VStack align="end" spacing={0}>
        <Text fontSize="md" fontWeight="medium" color="green.600">
          {formatCurrency({ number: site.totalAllocated ?? 0, maxFractionDigits: 0 })}
        </Text>
        <Text fontSize="xs" color="fg.muted">
          {formatCurrency({
            number: site.totalUsers ? (site.totalAllocated ?? 0) / site.totalUsers : 0,
            maxFractionDigits: 0,
          })}
          <Trans>per user</Trans>
        </Text>
      </VStack>
    </HStack>
  );
}

export function SiteLeaderboardWidget() {
  const { filters, displayMonth, canGoNext, goToPreviousMonth, goToNextMonth } =
    useMonthNavigation(DEFAULT_ORDER_BY);

  const currentLimit = DEFAULT_PAGE_SIZE;

  const { sites, error } = useSiteLeaderboard(filters, currentLimit);

  if (error) {
    return (
      <Center p={8}>
        <Text color="red.500" textAlign="center">
          <Trans>Error loading data. Please try again.</Trans>
        </Text>
      </Center>
    );
  }

  return (
    <Box bg="bg.surface" px={4} pb={4} borderRadius="md" boxShadow="sm">
      <Text fontSize="xl" fontWeight="semibold" color="fg.muted" whiteSpace="nowrap">
        <Trans>Sites Leaderboard</Trans>
      </Text>
      <Flex mt={2} align="center" direction="column" w="100%" gap={2}>
        <Box w="100%">
          <HStack mt={2} mb={4} w="full" gap={2} align="center" justify="space-between">
            <Text fontSize="sm" fontWeight="medium" color="fg.muted">
              {displayMonth}
            </Text>
            <HStack spacing={1} ml="auto">
              <IconButton
                size="xs"
                bg="gray.100"
                aria-label="Previous month"
                color="fg.default"
                icon={<ChevronLeft size={12} />}
                onClick={goToPreviousMonth}
              />
              <IconButton
                size="xs"
                bg="gray.100"
                color={canGoNext ? 'fg.default' : 'fg.muted'}
                aria-label="Next month"
                icon={<ChevronRight size={12} />}
                onClick={goToNextMonth}
                isDisabled={!canGoNext}
              />
            </HStack>
          </HStack>
        </Box>
        {Array.isArray(sites) && sites.length > 0 ? (
          <>
            {sites.map((site) => (
              <SitedLeaderboardWidgetRow site={site} key={site.siteId} />
            ))}
            <Box mt={2} w="full">
              <Button
                key="canManageCauses"
                size="md"
                w="100%"
                mt={2}
                variant="outline"
                as={Link}
                to={`/group_rewards/settings`}
              >
                <Trans>View Leaderboard</Trans>
              </Button>
            </Box>
          </>
        ) : (
          <Center p={8}>
            <Text textAlign="center">
              <Trans>No sites found</Trans>
            </Text>
          </Center>
        )}
      </Flex>
    </Box>
  );
}
